import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import type { MenuItemProps as MenuItem } from '@rio-cloud/rio-uikit/components/menuItems/MenuItem';
import Table, {
    TableBody,
    TableColumn,
    TableHeader,
    TableHeaderColumn,
    TableHeaderRow,
    TableRow,
} from '@rio-cloud/rio-uikit/Table';
import classNames from 'classnames';
import { type ReactNode, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { DiscountIdentifier } from '../../common/discount/DiscountService';
import type { AssetDiscount } from '../../common/discount/redux/types';
import { ProductTypedFormattedMessage } from '../../common/product/ProductTypedFormattedMessage';
import { ProductType } from '../../common/product/product';
import { isFreeTrialLevel } from '../../common/termedLevel/freeTrialLevel';
import { isTermedLevelSelectable } from '../../common/termedLevel/termedLevelSelectionValidationService';
import { PeriodUnit, type TermedLevel } from '../../common/termedLevel/types';
import { DATE_FORMAT } from '../../common/utils/dateUtil';
import { formatDurationUnit } from '../../common/utils/formatDurationUnit';
import { getRenewsToProductLabelFromContract } from '../../common/utils/productLabelUtil';
import { ContractResourceName } from '../contracts/ContractResourceName';
import { ContractState } from '../contracts/ContractState';
import { useSorting } from '../contracts/useSorting';

interface Props {
    contracts: TermedLevel[];
    productType: ProductType;
    canConfigureRenewal: boolean;
    findDiscount: (identifier: DiscountIdentifier) => AssetDiscount | undefined;
    legacyLevels: ReadonlySet<string>;
    onConfigureRenewal: (contract: TermedLevel) => void;
}

interface Column {
    id: string;
    propertyName: keyof TermedLevel;
    label: ReactNode;
    width?: number;
}

function getColumns(productType: ProductType): Column[] {
    return [
        {
            id: 'resourceName',
            propertyName: 'resourceName',
            label: (
                <ProductTypedFormattedMessage
                    productType={productType}
                    userBasedId='intl-msg:marketplace.contracts.user.table.header'
                    assetBasedId='intl-msg:marketplace.contracts.asset.table.header'
                />
            ),
            width: 200,
        },
        {
            id: 'variantName',
            propertyName: 'variantName',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.variant' />,
            width: 90,
        },
        {
            id: 'periodLength',
            propertyName: 'periodLength',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.period' />,
            width: 140,
        },
        {
            id: 'startsAt',
            propertyName: 'startsAt',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.startDate' />,
            width: 150,
        },
        {
            id: 'endsAt',
            propertyName: 'endsAt',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.endDate' />,
            width: 150,
        },
        {
            id: 'remainingDays',
            propertyName: 'remainingDays',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.daysLeft' />,
            width: 130,
        },
        {
            id: 'contractState',
            propertyName: 'contractState',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.status' />,
            width: 125,
        },
        {
            id: 'renewsToLevelName',
            propertyName: 'renewsToLevelName',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.renewsTo' />,
            width: 130,
        },
    ];
}

function ContractPeriod({
    contract,
    discount,
    isLegacy,
}: {
    contract: TermedLevel;
    discount?: AssetDiscount;
    isLegacy: boolean;
}) {
    const intl = useIntl();
    const isFreeTrial = isFreeTrialLevel(contract.level);

    return (
        <div className='display-flex align-items-center gap-5'>
            <span className='label label-condensed label-default white-space-nowrap'>
                {contract.periodLength !== undefined && contract.periodUnit !== undefined
                    ? `${contract.periodLength} ${formatDurationUnit(contract.periodUnit, intl, contract.periodLength)}`
                    : '--'}
            </span>
            {isLegacy && <span className='label label-condensed label-info white-space-nowrap'>Legacy</span>}
            {isFreeTrial && (
                <span className='label label-condensed label-secondary white-space-nowrap'>
                    <FormattedMessage id='intl-msg:marketplace.free.trial' />
                </span>
            )}
            {!isFreeTrial && discount && (
                <span className='label label-condensed label-success white-space-nowrap'>
                    -{Math.round(discount.discountPercentage)}%
                </span>
            )}
        </div>
    );
}

function ContractDate({ value }: { value?: string }) {
    const intl = useIntl();
    return value ? intl.formatDate(value, DATE_FORMAT) : <span>–</span>;
}

const getOverviewVariantName = (contract: TermedLevel): string => contract.variantShortName?.trim() ?? '';

function ContractVariant({ contract }: { contract: TermedLevel }) {
    const variantName = getOverviewVariantName(contract);
    return variantName ? <span className='label label-condensed label-primary label-filled'>{variantName}</span> : null;
}

function RemainingDays({ contract }: { contract: TermedLevel }) {
    if (contract.startsAt && !contract.endsAt) {
        return <span>∞</span>;
    }
    if (contract.remainingDays === undefined) {
        return <span>–</span>;
    }
    if (contract.remainingDays === 0) {
        return (
            <span className='text-color-warning text-medium'>
                <FormattedMessage id='intl-msg:marketplace.today' />
            </span>
        );
    }
    return (
        <span className={classNames({ 'text-color-warning text-medium': contract.criticalRemainingDays })}>
            {contract.remainingDays}{' '}
            <FormattedMessage
                id={
                    contract.remainingDays === 1
                        ? 'intl-msg:marketplace.durationUnit.day'
                        : 'intl-msg:marketplace.durationUnit.days'
                }
            />
        </span>
    );
}

function BookingOverviewContractState({ contract }: { contract: TermedLevel }) {
    if (contract.hasInactiveActivationState) {
        return (
            <div className='label label-filled label-condensed label-default'>
                <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.status.deleted' />
            </div>
        );
    }

    return <ContractState contractState={contract.contractState} />;
}

function RenewalDropdown({
    contract,
    variantName,
    canConfigureRenewal,
    onConfigureRenewal,
}: {
    contract: TermedLevel;
    variantName: string;
    canConfigureRenewal: boolean;
    onConfigureRenewal: (contract: TermedLevel) => void;
}) {
    const intl = useIntl();
    const periodOptions = [
        intl.formatMessage({ id: 'intl-msg:marketplace.p1d.period.label' }),
        `12 ${formatDurationUnit(PeriodUnit.MONTH, intl, 12)}`,
        `24 ${formatDurationUnit(PeriodUnit.MONTH, intl, 24)}`,
    ];
    const noRenewalOption = intl.formatMessage({ id: 'intl-msg:marketplace.contracts.noRenew' });
    const optionLabels =
        variantName === 'S' || variantName === 'M'
            ? [noRenewalOption, ...['S', 'M'].flatMap(variant => periodOptions.map(period => `${variant} | ${period}`))]
            : [...periodOptions, noRenewalOption];
    const items: MenuItem[] = optionLabels.map(value => ({
        value,
        onSelect: () => onConfigureRenewal(contract),
    }));
    const isRenewalConfigurable = canConfigureRenewal && isTermedLevelSelectable(contract);
    const renewalVariantName = contract.renewsToLevelVariantShortName?.trim();
    const title =
        contract.renewsToLevel === undefined ? (
            <FormattedMessage id='intl-msg:marketplace.contracts.notRenewed' />
        ) : renewalVariantName ? (
            getRenewsToProductLabelFromContract(contract, intl, renewalVariantName)
        ) : null;

    return (
        <div
            className={classNames({ 'cursor-not-allowed': !isRenewalConfigurable })}
            onClick={event => event.stopPropagation()}
        >
            <ButtonDropdown
                aria-label={`${intl.formatMessage({
                    id: 'intl-msg:marketplace.bookings.overview.table.renewsTo',
                })}: ${contract.resourceName ?? contract.resourceId}`}
                title={title}
                items={items}
                bsStyle='default'
                toggleButtonType='label'
                toggleClassName={classNames('label-condensed white-space-nowrap', {
                    'opacity-50': !isRenewalConfigurable,
                })}
                dropdownClassName='width-auto'
                disabled={!isRenewalConfigurable}
            />
        </div>
    );
}

function ContractTableRow({
    contract,
    productType,
    canConfigureRenewal,
    findDiscount,
    legacyLevels,
    onConfigureRenewal,
}: {
    contract: TermedLevel;
    productType: ProductType;
    canConfigureRenewal: boolean;
    findDiscount: (identifier: DiscountIdentifier) => AssetDiscount | undefined;
    legacyLevels: ReadonlySet<string>;
    onConfigureRenewal: (contract: TermedLevel) => void;
}) {
    const variantName = getOverviewVariantName(contract);
    const isDeleted = contract.hasInactiveActivationState;
    const discount = findDiscount({
        assetId: contract.resourceId,
        productId: contract.productId,
        productLevel: contract.level,
    });

    return (
        <TableRow
            id={`${contract.productId}-${contract.resourceId}-${contract.level}-${contract.createdAt}`}
            className={classNames({ 'opacity-50 bg-lightest': isDeleted })}
            disabled={isDeleted}
        >
            <TableColumn columnKey='resourceName'>
                <span
                    className={classNames(
                        'uikit-icon',
                        productType === ProductType.USER_BASED ? 'icon-user' : 'icon-truck',
                        'margin-right-5'
                    )}
                    aria-hidden={true}
                />
                {isDeleted ? (
                    contract.resourceName || contract.resourceId
                ) : (
                    <ContractResourceName contract={contract} />
                )}
            </TableColumn>
            <TableColumn columnKey='variantName'>
                <ContractVariant contract={contract} />
            </TableColumn>
            <TableColumn columnKey='periodLength'>
                <ContractPeriod contract={contract} discount={discount} isLegacy={legacyLevels.has(contract.level)} />
            </TableColumn>
            <TableColumn columnKey='startsAt'>
                <ContractDate value={contract.startsAt} />
            </TableColumn>
            <TableColumn columnKey='endsAt'>
                <ContractDate value={contract.endsAt} />
            </TableColumn>
            <TableColumn columnKey='remainingDays'>
                <RemainingDays contract={contract} />
            </TableColumn>
            <TableColumn columnKey='contractState' className='text-capitalize'>
                <BookingOverviewContractState contract={contract} />
            </TableColumn>
            <TableColumn columnKey='renewsToLevelName' preventRowClick>
                <RenewalDropdown
                    contract={contract}
                    variantName={variantName}
                    canConfigureRenewal={canConfigureRenewal}
                    onConfigureRenewal={onConfigureRenewal}
                />
            </TableColumn>
        </TableRow>
    );
}

export function BookingOverviewContractTable({
    contracts,
    productType,
    canConfigureRenewal,
    findDiscount,
    legacyLevels,
    onConfigureRenewal,
}: Props) {
    const { sort, handleSortChange, sortBy, direction } = useSorting<TermedLevel>('resourceName');
    const columns = useMemo(() => getColumns(productType), [productType]);

    return (
        <Table
            rowKey='id'
            hover
            border={false}
            rounded={false}
            className='rounded-bottom'
            sortBy={String(sortBy)}
            sortDirection={direction}
            onSortChange={columnKey => {
                const column = columns.find(({ id }) => id === columnKey);
                if (column) {
                    handleSortChange(column.propertyName);
                }
            }}
        >
            <TableHeader>
                <TableHeaderRow>
                    {columns.map(column => (
                        <TableHeaderColumn
                            key={column.id}
                            columnKey={column.id}
                            width={column.width}
                            sortable
                            resizeable
                        >
                            {column.label}
                        </TableHeaderColumn>
                    ))}
                </TableHeaderRow>
            </TableHeader>
            <TableBody maxHeight={290}>
                {sort(contracts).map(contract => (
                    <ContractTableRow
                        key={`${contract.productId}-${contract.resourceId}-${contract.level}-${contract.createdAt}`}
                        contract={contract}
                        productType={productType}
                        canConfigureRenewal={canConfigureRenewal}
                        findDiscount={findDiscount}
                        legacyLevels={legacyLevels}
                        onConfigureRenewal={onConfigureRenewal}
                    />
                ))}
            </TableBody>
        </Table>
    );
}
